import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import Editor from '@monaco-editor/react'
import { Button } from './ui/button'
import {
  loadDatabase,
  saveDatabase,
  executeQuery as localExecuteQuery,
  loadHistoryFromIndexedDB,
  clearHistoryInIndexedDB,
  loadSavedQueriesFromIndexedDB,
  saveQueryToIndexedDB,
  deleteSavedQueryFromIndexedDB,
  updateSavedQueryLabelInIndexedDB,
  syncSavedQueriesFromSqlite
} from '../database/db'
import { Card } from './ui/card'
import { ScrollArea } from './ui/scroll-area'
import { Separator } from './ui/separator'
import { toast } from 'sonner'
import { Switch } from './ui/switch'
import { Badge } from './ui/badge'
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs'
import {
  Database,
  Play,
  RotateCcw,
  History,
  LogOut,
  User,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  Table as TableIcon,
  RefreshCw as Sync, // Using RefreshCw as a suitable icon for Sync
  Download,
  Upload,
  Sparkles, // Changed icon to Sparkles for AI Assistant
  Loader2, // Added Loader2 for loading indicator
  Bookmark,
  Tag,
  Pencil,
  Settings,
  Palette,
  Check,
  Terminal
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from './ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from './ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from './ui/dialog'
import { Input } from './ui/input'
import axios from 'axios' // Import axios for backend API calls
import { useTheme } from '../context/ThemeContext'

// Suppress ResizeObserver loop errors in development
if (process.env.NODE_ENV === 'development') {
  const originalError = console.error
  console.error = (...args) => {
    if (
      args[0] &&
      typeof args[0] === 'string' &&
      args[0].includes(
        'ResizeObserver loop completed with undelivered notifications'
      )
    ) {
      return
    }
    originalError.apply(console, args)
  }
}

const TypewriterText = ({ text, speed = 12, onComplete, isCode = false, className = '' }) => {
  const [displayedText, setDisplayedText] = useState('')
  const [isTyping, setIsTyping] = useState(true)

  const onCompleteRef = useRef(onComplete)
  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  useEffect(() => {
    if (!text) {
      setDisplayedText('')
      setIsTyping(false)
      return
    }

    setDisplayedText('')
    setIsTyping(true)

    let index = 0
    const step = text.length > 200 ? 3 : text.length > 80 ? 2 : 1
    const timer = setInterval(() => {
      index += step
      if (index >= text.length) {
        setDisplayedText(text)
        setIsTyping(false)
        clearInterval(timer)
        if (onCompleteRef.current) {
          onCompleteRef.current()
        }
      } else {
        setDisplayedText(text.slice(0, index))
      }
    }, speed)

    return () => clearInterval(timer)
  }, [text, speed])

  if (isCode) {
    return (
      <pre className={`bg-slate-950 p-3 rounded-md font-mono text-xs text-green-400 overflow-x-auto border border-purple-900/40 whitespace-pre-wrap break-all relative ${className}`}>
        <code>{displayedText}</code>
        {isTyping && (
          <span className='inline-block w-1.5 h-3.5 ml-1 bg-purple-400 animate-pulse align-middle rounded-xs' />
        )}
      </pre>
    )
  }

  return (
    <span className={className}>
      {displayedText}
      {isTyping && (
        <span className='inline-block w-1.5 h-3.5 ml-0.5 bg-purple-400 animate-pulse align-middle rounded-xs' />
      )}
    </span>
  )
}

const WorkspacePage = ({ user, onLogout }) => {
  const navigate = useNavigate()
  const { theme, setTheme, activeThemeObj, themes } = useTheme()
  const editorRef = useRef(null)
  const dbRef = useRef(null) // Ref to hold the sql.js database instance
  const executeQueryRef = useRef(null) // Ref to hold the latest executeQuery function
  const fileInputRef = useRef(null) // Ref for the hidden file input

  const [query, setQuery] = useState('')
  const [queryResult, setQueryResult] = useState(null)
  const [history, setHistory] = useState([])
  const [savedQueries, setSavedQueries] = useState([])
  const [sidebarTab, setSidebarTab] = useState('history') // 'history' | 'saved'

  const [schema, setSchema] = useState({})
  const [executing, setExecuting] = useState(false)
  const [dbLoaded, setDbLoaded] = useState(false) // New state to track DB loading
  const [showResetDialog, setShowResetDialog] = useState(false)
  const [showClearHistoryDialog, setShowClearHistoryDialog] = useState(false)
  const [showAiPromptDialog, setShowAiPromptDialog] = useState(false) // State for AI prompt dialog
  const [showSettingsDialog, setShowSettingsDialog] = useState(false) // State for Settings dialog
  const [aiPrompt, setAiPrompt] = useState('') // State for AI prompt input
  const [generatingSql, setGeneratingSql] = useState(false) // State for SQL generation loading
  const [aiErrorAnalysis, setAiErrorAnalysis] = useState(null) // State for AI error analysis
  const [causeFinished, setCauseFinished] = useState(false) // State for streaming animation sequence
  const [analysisTypingDone, setAnalysisTypingDone] = useState(false) // State tracking if AI response typing animation is complete
  const handleCauseComplete = useCallback(() => {
    setCauseFinished(true)
    if (!aiErrorAnalysis?.corrected_query) {
      setAnalysisTypingDone(true)
    }
  }, [aiErrorAnalysis])
  const [autoExplainError, setAutoExplainError] = useState(() => {
    return localStorage.getItem('datapilot-auto-explain-error') === 'true'
  })

  const handleToggleAutoExplainError = (enabled) => {
    setAutoExplainError(enabled)
    localStorage.setItem('datapilot-auto-explain-error', enabled ? 'true' : 'false')
    toast.info(enabled ? 'AI Error Auto-Diagnosis enabled' : 'AI Error Diagnosis set to On-Demand')
  }

  // Save Query dialog state
  const [showSaveQueryDialog, setShowSaveQueryDialog] = useState(false)
  const [selectedHistoryQuery, setSelectedHistoryQuery] = useState('')
  const [saveLabelInput, setSaveLabelInput] = useState('')
  const [isSavingQuery, setIsSavingQuery] = useState(false)

  // Edit Label dialog state
  const [showEditLabelDialog, setShowEditLabelDialog] = useState(false)
  const [editingSavedQueryItem, setEditingSavedQueryItem] = useState(null)
  const [editLabelInput, setEditLabelInput] = useState('')

  // Delete Saved Query confirmation dialog state
  const [showDeleteSavedDialog, setShowDeleteSavedDialog] = useState(false)
  const [deletingSavedQueryId, setDeletingSavedQueryId] = useState(null)

  const loadSavedQueries = useCallback(async () => {
    try {
      const data = await loadSavedQueriesFromIndexedDB()
      setSavedQueries(data.reverse())
    } catch (error) {
      console.error('Failed to load saved queries', error)
    }
  }, [])

  // Initialize database and load history & saved queries on component mount
  useEffect(() => {
    const initDb = async () => {
      console.log("Initializing database...");
      try {
        const database = await loadDatabase()
        dbRef.current = database
        setDbLoaded(true)
        loadSchema()
        loadHistory()
        const synced = await syncSavedQueriesFromSqlite(database)
        setSavedQueries((synced || []).reverse())
        console.log("Database initialized and loaded:", dbRef.current);
      } catch (error) {
        console.error("Failed to initialize database:", error);
        toast.error("Failed to load database. Please refresh the page.");
      }
    }
    initDb()
  }, [])

  const handleEditorDidMount = useCallback(editor => {
    editorRef.current = editor
    editor.addCommand(
      window.monaco.KeyMod.CtrlCmd | window.monaco.KeyCode.Enter,
      () => {
        executeQueryRef.current()
      },
      null
    )
  }, [])

  const loadHistory = useCallback(async () => {
    try {
      const historyData = await loadHistoryFromIndexedDB()
      setHistory(historyData.reverse()) // Display most recent first
    } catch (error) {
      console.error('Failed to load history from IndexedDB', error)
      toast.error('Failed to load query history')
    }
  }, [])

  const loadSchema = useCallback(async () => {
    if (!dbRef.current) return

    try {
      const result = dbRef.current.exec(
        "SELECT name FROM sqlite_master WHERE type='table';"
      )
      const tableNames =
        result[0]?.values
          .map(row => row[0])
          .filter(name => name !== 'sqlite_sequence' && !name.startsWith('_datapilot_')) || []

      const schemaData = {}
      for (const tableName of tableNames) {
        const tableInfo = dbRef.current.exec(`PRAGMA table_info(${tableName});`)
        schemaData[tableName] = tableInfo[0]?.values.map(row => ({
          cid: row[0],
          name: row[1],
          type: row[2],
          notnull: row[3],
          dflt_value: row[4],
          pk: row[5]
        })) || []
      }
      setSchema(schemaData)
    } catch (error) {
      console.error('Failed to load schema from local DB', error)
    }
  }, [])

  const handleExplainError = useCallback(async (overrideQuery, overrideError) => {
    const targetQuery = (typeof overrideQuery === 'string' && overrideQuery.trim()) ? overrideQuery : query
    const targetError = (typeof overrideError === 'string' && overrideError.trim()) ? overrideError : queryResult?.error

    if (!targetQuery || !targetError) return

    setCauseFinished(false)
    setAnalysisTypingDone(false)
    setAiErrorAnalysis({ loading: true, cause: '', corrected_query: '', error: '' })
    try {
      const token = localStorage.getItem("token")
      if (!token) {
        throw new Error("Authentication token not found. Please log in.")
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/v2/explain-error/`,
        {
          query: targetQuery,
          error: targetError,
          schema: schema
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      setAiErrorAnalysis({
        loading: false,
        cause: response.data.cause,
        corrected_query: response.data.corrected_query,
        error: ''
      })
      toast.success('Error analyzed by AI!')
    } catch (err) {
      console.error('Error analyzing SQL error:', err)
      const errMsg = err.response?.data?.error || err.message || 'Failed to analyze error.'
      setAiErrorAnalysis({
        loading: false,
        cause: '',
        corrected_query: '',
        error: errMsg
      })
      toast.error(errMsg)
    }
  }, [query, queryResult, schema])

  const executeQuery = useCallback(async () => {
    if (!query.trim()) {
      toast.error('Please enter a query')
      return
    }
    if (!dbRef.current) {
      toast.error('Database not loaded yet.')
      return
    }

    setExecuting(true)
    setAiErrorAnalysis(null)
    setCauseFinished(false)
    setAnalysisTypingDone(false)
    try {
      const { success, result, error, executionTime, affectedRows } = await localExecuteQuery(query, 'mysql')
      console.log("Result from localExecuteQuery:", { success, result, error, executionTime, affectedRows });

      if (success) {
        setQueryResult({
          success: true,
          columns: result[0]?.columns || [],
          rows: result[0]?.values || [],
          affected_rows: affectedRows,
          execution_time_ms: parseFloat(executionTime)
        })
        toast.success(`Query executed in ${executionTime}ms`)

        const upperCaseQuery = query.trim().toUpperCase();
        if (upperCaseQuery.startsWith('CREATE TABLE') || upperCaseQuery.startsWith('DROP TABLE')) {
          loadSchema()
        }
      } else {
        setQueryResult({ success: false, error })
        toast.error(error)
        if (autoExplainError) {
          handleExplainError(query, error)
        }
      }

      loadHistory()
    } catch (error) {
      toast.error('Query execution failed: ' + error.message)
    } finally {
      setExecuting(false)
    }
  }, [query, loadSchema, loadHistory, autoExplainError, handleExplainError])

  // Update the ref whenever executeQuery changes
  useEffect(() => {
    executeQueryRef.current = executeQuery
  }, [executeQuery])

  const resetDatabase = async () => {
    try {
      const newDb = await loadDatabase(true)
      dbRef.current = newDb
      await saveDatabase()
      toast.success('Database reset successfully')
      setQueryResult(null)
      loadSchema()
      loadSavedQueries()
      setShowResetDialog(false)
    } catch (error) {
      console.error('Failed to reset local database', error)
      toast.error('Failed to reset database')
    }
  }

  const handleDownloadDb = async () => {
    if (!dbRef.current) {
      toast.error('Database not loaded yet.')
      return
    }
    try {
      await saveDatabase()
      const binaryArray = dbRef.current.export()
      const blob = new Blob([binaryArray], { type: 'application/x-sqlite3' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'datapilot.db'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast.success('Database downloaded successfully!')
    } catch (error) {
      console.error('Failed to download database', error)
      toast.error('Failed to download database')
    }
  }

  const handleUploadDb = async event => {
    const file = event.target.files[0]
    if (!file) {
      return
    }

    const reader = new FileReader()
    reader.onload = async e => {
      try {
        const U8Array = new Uint8Array(e.target.result)
        const newDb = await loadDatabase(false, U8Array)
        dbRef.current = newDb
        await saveDatabase()
        toast.success('Database uploaded successfully!')
        setQueryResult(null)
        loadSchema()
        const synced = await syncSavedQueriesFromSqlite(newDb)
        setSavedQueries((synced || []).reverse())
      } catch (error) {
        console.error('Failed to upload database', error)
        toast.error('Failed to upload database. Make sure it is a valid SQLite .db file.')
      }
    }
    reader.readAsArrayBuffer(file)
  }

  const clearHistory = async () => {
    try {
      await clearHistoryInIndexedDB()
      setHistory([])
      toast.success('Query history cleared')
      setShowClearHistoryDialog(false)
    } catch (error) {
      console.error('Failed to clear history from IndexedDB', error)
      toast.error('Failed to clear query history')
    }
  }

  const loadQueryFromHistory = historyQuery => {
    setQuery(historyQuery)
    toast.info('Query loaded from history')
  }

  // Save Query Handlers
  const openSaveDialog = (queryText) => {
    setSelectedHistoryQuery(queryText)
    setSaveLabelInput('')
    setShowSaveQueryDialog(true)
  }

  const handleSaveQuerySubmit = async () => {
    if (!saveLabelInput.trim()) {
      toast.error('Please enter a label for the query')
      return
    }
    setIsSavingQuery(true)
    try {
      await saveQueryToIndexedDB(
        { query: selectedHistoryQuery, label: saveLabelInput.trim() },
        dbRef.current
      )
      toast.success('Query saved successfully!')
      setShowSaveQueryDialog(false)
      setSelectedHistoryQuery('')
      setSaveLabelInput('')
      loadSavedQueries()
    } catch (err) {
      console.error('Failed to save query', err)
      toast.error('Failed to save query')
    } finally {
      setIsSavingQuery(false)
    }
  }

  // Edit Label Handlers
  const openEditLabelDialog = (item) => {
    setEditingSavedQueryItem(item)
    setEditLabelInput(item.label || '')
    setShowEditLabelDialog(true)
  }

  const handleUpdateLabelSubmit = async () => {
    if (!editLabelInput.trim()) {
      toast.error('Please enter a valid label')
      return
    }
    try {
      await updateSavedQueryLabelInIndexedDB(
        editingSavedQueryItem.id,
        editLabelInput.trim(),
        dbRef.current
      )
      toast.success('Label updated successfully!')
      setShowEditLabelDialog(false)
      setEditingSavedQueryItem(null)
      loadSavedQueries()
    } catch (err) {
      console.error('Failed to update label', err)
      toast.error('Failed to update label')
    }
  }

  // Delete Saved Query Handlers
  const confirmDeleteSavedQuery = (id) => {
    setDeletingSavedQueryId(id)
    setShowDeleteSavedDialog(true)
  }

  const handleDeleteSavedQuery = async () => {
    if (!deletingSavedQueryId) return
    try {
      await deleteSavedQueryFromIndexedDB(deletingSavedQueryId, dbRef.current)
      toast.success('Saved query deleted')
      setShowDeleteSavedDialog(false)
      setDeletingSavedQueryId(null)
      loadSavedQueries()
    } catch (err) {
      console.error('Failed to delete saved query', err)
      toast.error('Failed to delete saved query')
    }
  }

  const handleGenerateSql = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Please enter a prompt for the AI Assistant.')
      return
    }

    setGeneratingSql(true)
    const startTime = Date.now();
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token not found. Please log in.");
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/v2/generate-sql/`,
        { prompt: aiPrompt, schema: schema },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const generatedSql = response.data.sql_query;
      const endTime = Date.now();
      const duration = endTime - startTime;

      setQuery(prevQuery => (prevQuery.trim() ? prevQuery + '\n' + generatedSql : generatedSql))
      toast.success(`SQL query generated successfully in ${duration}ms!`)

    } catch (error) {
      console.error('Error generating SQL:', error)
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      const formattedMessage = errorMessage.toLowerCase().includes('failed to generate sql query')
        ? errorMessage
        : `Failed to generate SQL query: ${errorMessage}`;
      toast.error(formattedMessage)
    } finally {
      setGeneratingSql(false)
      setAiPrompt('')
      setShowAiPromptDialog(false)
    }
  }

  return (
    <div className={activeThemeObj.styles?.appBg || 'min-h-screen bg-gradient-to-br from-slate-50 to-blue-50'}>
      {/* Header */}
      <header
        className={`border-b sticky top-0 z-50 transition-colors duration-300 ${activeThemeObj.styles?.headerBg || 'border-slate-200 bg-white/80 backdrop-blur-md'}`}
        data-testid='workspace-header'
      >
        <div className='container mx-auto px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-between gap-2 max-w-7xl'>
          <div className='flex items-center gap-2 sm:gap-3 min-w-0'>
            <div className='h-9 w-9 sm:h-10 sm:w-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-md flex-shrink-0'>
              <Database className='h-4 w-4 sm:h-5 sm:w-5 text-white' />
            </div>
            <div className='min-w-0'>
              <div className='flex items-center gap-2'>
                <h1 className='text-base sm:text-xl font-bold truncate'>DataPilot v2</h1>
                {Boolean(user?.is_staff) && (
                  <button
                    onClick={() => navigate('/dev')}
                    data-testid='dev-mode-chip'
                    className='cursor-pointer group focus:outline-none'
                    title='Open Dev Console & Deployment Manager'
                  >
                    <Badge
                      variant='outline'
                      className='px-2 py-0.5 text-[10px] sm:text-xs font-mono font-bold tracking-wider border border-blue-500/30 dark:border-blue-500/40 bg-blue-500/10 dark:bg-blue-500/15 text-blue-600 dark:text-blue-400 group-hover:bg-blue-500/25 group-hover:border-blue-500/60 transition-all flex items-center gap-1.5 rounded-md shadow-xs flex-shrink-0'
                    >
                      <span className='w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-blue-500 dark:bg-blue-400 animate-pulse flex-shrink-0' />
                      DEV MODE
                    </Badge>
                  </button>
                )}
              </div>
              <p className='text-[10px] sm:text-xs opacity-75 hidden sm:block truncate'>SQL Learning Workspace</p>
            </div>
          </div>

          <div className='flex items-center gap-1.5 sm:gap-2 flex-wrap sm:flex-nowrap justify-end'>
            <input
              type='file'
              ref={fileInputRef}
              onChange={handleUploadDb}
              style={{ display: 'none' }}
              accept='.db,.sqlite,.sqlite3'
            />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant='ghost'
                  size='sm'
                  data-testid='user-menu-button'
                  className='px-2 sm:px-3 text-xs sm:text-sm h-8 sm:h-9 max-w-[110px] sm:max-w-[180px] truncate'
                >
                  <User className='h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2 flex-shrink-0' />
                  <span className='truncate'>{user?.full_name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end' className='w-52 p-1.5'>
                <div className='px-2 py-2 text-sm border-b mb-1 border-border/50'>
                  <div className='font-medium truncate'>{user?.full_name}</div>
                  {user?.email && <div className='text-xs opacity-70 truncate'>{user.email}</div>}
                  {user?.phone_number && <div className='text-xs opacity-70 truncate'>{user.phone_number}</div>}
                </div>
                <DropdownMenuItem
                  onClick={handleDownloadDb}
                  data-testid='download-db-button'
                  className='cursor-pointer flex items-center gap-2 py-2 px-2.5 rounded-md hover:bg-accent'
                >
                  <Download className='h-4 w-4' />
                  <span>Download DB</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => fileInputRef.current.click()}
                  data-testid='upload-db-button'
                  className='cursor-pointer flex items-center gap-2 py-2 px-2.5 rounded-md hover:bg-accent'
                >
                  <Upload className='h-4 w-4' />
                  <span>Upload DB</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setShowResetDialog(true)}
                  data-testid='reset-db-button'
                  className='cursor-pointer flex items-center gap-2 py-2 px-2.5 rounded-md hover:bg-accent'
                >
                  <RotateCcw className='h-4 w-4' />
                  <span>Reset DB</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator className='my-1' />
                {Boolean(user?.is_staff) && (
                  <DropdownMenuItem
                    onClick={() => navigate('/dev')}
                    data-testid='dev-console-menu-item'
                    className='cursor-pointer flex items-center gap-2 py-2 px-2.5 rounded-md hover:bg-blue-500/10 text-blue-600 dark:text-blue-400 focus:text-blue-600 dark:focus:text-blue-400 font-medium'
                  >
                    <Terminal className='h-4 w-4' />
                    <span>Dev Console</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => setShowSettingsDialog(true)}
                  data-testid='settings-button'
                  className='cursor-pointer flex items-center gap-2 py-2 px-2.5 rounded-md hover:bg-accent'
                >
                  <Settings className='h-4 w-4' />
                  <span>Settings</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator className='my-1' />
                <DropdownMenuItem
                  onClick={onLogout}
                  data-testid='logout-button'
                  className='cursor-pointer flex items-center gap-2 py-2 px-2.5 rounded-md hover:bg-destructive/10 text-destructive focus:text-destructive'
                >
                  <LogOut className='h-4 w-4' />
                  <span>Logout</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className='container mx-auto p-3 sm:p-4 max-w-7xl'>
        <div
          className='grid grid-cols-1 lg:grid-cols-12 gap-4'
          data-testid='workspace-grid'
        >
          {/* Main content */}
          <div className='lg:col-span-8 space-y-4 min-w-0'>
            {/* Database Schema */}
            <Card
              className={`p-3 sm:p-4 border transition-colors duration-300 min-w-0 ${activeThemeObj.styles?.cardBg || 'bg-white border-slate-200'}`}
              data-testid='schema-panel'
            >
              <div className='flex items-center gap-2 mb-3'>
                <TableIcon className='h-4 w-4 sm:h-5 sm:w-5 text-blue-500 flex-shrink-0' />
                <h2 className='font-semibold text-sm sm:text-base'>
                  Database Schema
                </h2>
              </div>
              <div className='flex gap-2 sm:gap-3 flex-wrap'>
                {Object.keys(schema).length === 0 ? (
                  <p className='text-xs sm:text-sm opacity-70'>No tables found</p>
                ) : (
                  Object.keys(schema).map(tableName => (
                    <div
                      key={tableName}
                      className={`px-2.5 sm:px-3 py-1 sm:py-1.5 border rounded-lg text-xs sm:text-sm max-w-full break-words ${activeThemeObj.styles?.badgeBg || 'bg-blue-50 border-blue-200 text-blue-900'}`}
                      data-testid={`schema-table-${tableName}`}
                    >
                      <span className='font-medium'>
                        {tableName}
                      </span>
                      <span className='opacity-75 ml-1.5'>
                        ({schema[tableName].length} columns)
                      </span>
                    </div>
                  ))
                )}
              </div>
            </Card>

            {/* SQL Editor */}
            <Card
              className={`overflow-hidden border transition-colors duration-300 min-w-0 ${activeThemeObj.styles?.cardBg || 'bg-white border-slate-200'}`}
              data-testid='editor-panel'
            >
              <div className={`p-3 sm:p-4 border-b flex items-center justify-between gap-2 flex-wrap sm:flex-nowrap ${activeThemeObj.styles?.border || 'border-slate-200'}`}>
                <h2 className='font-semibold text-sm sm:text-base'>SQL Editor</h2>
                <div className='flex items-center gap-2 sm:gap-3'>
                  <Button
                    onClick={executeQuery}
                    disabled={executing || !dbLoaded}
                    size='sm'
                    data-testid='execute-query-button'
                    className='text-xs sm:text-sm'
                  >
                    <Play className='h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2' />
                    {executing ? 'Executing...' : (dbLoaded ? 'Execute Query' : 'Loading DB...')}
                  </Button>
                </div>
              </div>
              <div className='monaco-editor-wrapper relative w-full min-w-0'>
                <Button
                  size='icon'
                  className='rounded-full w-9 h-9 sm:w-10 sm:h-10 absolute top-3 right-3 z-10 bg-gradient-to-br from-purple-500 to-indigo-600 text-white shadow-lg hover:from-purple-600 hover:to-indigo-700 hover:shadow-2xl hover:shadow-purple-500/70 transition-all duration-200'
                  data-testid='ai-assistant-button'
                  onClick={() => setShowAiPromptDialog(true)}
                  title='AI SQL Assistant'
                >
                  <Sparkles className='h-5 w-5 sm:h-6 sm:w-6' />
                </Button>
                <Editor
                  height='250px'
                  defaultLanguage='sql'
                  value={query}
                  onChange={value => setQuery(value || '')}
                  onMount={handleEditorDidMount}
                  theme={activeThemeObj.monacoTheme || (activeThemeObj.isDark ? 'vs-dark' : 'vs')}
                  options={{
                    minimap: { enabled: false },
                    fontSize: 14,
                    lineNumbers: 'on',
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    wordWrap: 'on',
                    fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
                    lineHeight: 20,
                    padding: { top: 12, bottom: 12 }
                  }}
                />
              </div>
            </Card>

            {/* Query Results */}
            {queryResult && (
              <Card
                className={`p-3 sm:p-4 border transition-colors duration-300 min-w-0 ${activeThemeObj.styles?.cardBg || 'bg-white border-slate-200'}`}
                data-testid='results-panel'
              >
                <div className='flex items-center justify-between mb-3 gap-2'>
                  <h2 className='font-semibold text-sm sm:text-base'>
                    Query Results
                  </h2>
                  {queryResult.success ? (
                    <div className='flex items-center gap-1.5 text-xs sm:text-sm text-green-500'>
                      <CheckCircle className='h-4 w-4 flex-shrink-0' />
                      <span>Success</span>
                    </div>
                  ) : (
                    <div className='flex items-center gap-1.5 text-xs sm:text-sm text-red-500'>
                      <XCircle className='h-4 w-4 flex-shrink-0' />
                      <span>Error</span>
                    </div>
                  )}
                </div>

                {queryResult.success ? (
                  queryResult.columns ? (
                    <div className={`overflow-x-auto max-h-96 w-full border rounded-lg ${activeThemeObj.styles?.border || 'border-slate-200'}`}>
                      <table className='w-full text-xs sm:text-sm border-collapse min-w-full'>
                        <thead>
                          <tr className={`border-b ${activeThemeObj.isDark ? 'bg-slate-800/80 border-slate-700' : 'bg-slate-100 border-slate-200'}`}>
                            {queryResult.columns.map((col, idx) => (
                              <th
                                key={idx}
                                className={`border-r px-3 sm:px-4 py-2 text-left font-semibold whitespace-nowrap ${activeThemeObj.isDark ? 'border-slate-700' : 'border-slate-200'}`}
                                data-testid={`result-header-${col}`}
                              >
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {queryResult.rows.map((row, rowIdx) => (
                            <tr key={rowIdx} className={`border-b last:border-0 ${activeThemeObj.isDark ? 'border-slate-800 hover:bg-slate-800/50' : 'border-slate-200 hover:bg-slate-50'}`}>
                              {row.map((cell, cellIdx) => (
                                <td
                                  key={cellIdx}
                                  className={`border-r px-3 sm:px-4 py-2 whitespace-nowrap max-w-xs truncate ${activeThemeObj.isDark ? 'border-slate-800' : 'border-slate-200'}`}
                                  data-testid={`result-cell-${rowIdx}-${cellIdx}`}
                                  title={cell !== null ? String(cell) : 'NULL'}
                                >
                                  {cell !== null ? String(cell) : 'NULL'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className='text-xs sm:text-sm opacity-80'>
                      Query executed successfully. {queryResult.affected_rows}{' '}
                      row(s) affected.
                    </div>
                  )
                ) : (
                  <div className='space-y-3'>
                    <div className='p-3.5 bg-red-950/40 border border-red-800/60 text-red-300 rounded-lg text-xs sm:text-sm break-words flex flex-col gap-2.5'>
                      <div className='flex items-start gap-2'>
                        <XCircle className='h-4 w-4 text-red-400 mt-0.5 shrink-0' />
                        <span className='font-mono font-medium'>{queryResult.error}</span>
                      </div>
                      
                      {!aiErrorAnalysis && (
                        <div className='pt-1'>
                          <Button
                            size='sm'
                            variant='outline'
                            onClick={() => handleExplainError()}
                            className='bg-red-900/30 hover:bg-red-900/50 border-red-700/60 text-red-200 text-xs gap-1.5 transition-all'
                          >
                            <Sparkles className='h-3.5 w-3.5 text-purple-300' />
                            Explain & Fix Error with AI
                          </Button>
                        </div>
                      )}
                    </div>

                    {aiErrorAnalysis && (
                      <div className='p-4 bg-purple-950/30 border border-purple-800/50 rounded-lg text-xs sm:text-sm space-y-3 text-purple-200'>
                        <div className='flex items-center justify-between border-b border-purple-800/40 pb-2.5'>
                          <div className='flex items-center gap-2 font-semibold text-purple-300'>
                            <Sparkles className='h-4 w-4 text-purple-400 animate-pulse' />
                            <span>AI Error Diagnostics</span>
                          </div>
                          {aiErrorAnalysis.loading && (
                            <div className='flex items-center gap-1.5 text-xs text-purple-300/80'>
                              <Loader2 className='h-3.5 w-3.5 animate-spin' />
                              <span>Analyzing...</span>
                            </div>
                          )}
                        </div>

                        {aiErrorAnalysis.loading ? (
                          <div className='py-4 text-center text-purple-300/70 text-xs flex flex-col items-center gap-2'>
                            <Loader2 className='h-5 w-5 animate-spin text-purple-400' />
                            <span>Analyzing query error, intent, and schema...</span>
                          </div>
                        ) : aiErrorAnalysis.cause ? (
                          <div className='space-y-3'>
                            {analysisTypingDone ? (
                              <div className='space-y-3'>
                                <div>
                                  <div className='font-semibold text-purple-300 text-xs uppercase tracking-wider mb-1 flex items-center gap-1.5'>
                                    <Sparkles className='h-3 w-3 text-purple-400' />
                                    <span>Probable Cause & Reason</span>
                                  </div>
                                  <p className='bg-purple-950/50 p-3 rounded-md border border-purple-800/40 leading-relaxed text-xs sm:text-sm text-purple-100'>
                                    {aiErrorAnalysis.cause}
                                  </p>
                                </div>

                                {aiErrorAnalysis.corrected_query && (
                                  <div className='pt-1'>
                                    <div className='flex items-center justify-between mb-1.5'>
                                      <span className='font-semibold text-purple-300 text-xs uppercase tracking-wider flex items-center gap-1.5'>
                                        <Sparkles className='h-3 w-3 text-green-400' />
                                        <span>Suggested Correct Query</span>
                                      </span>
                                      <Button
                                        size='sm'
                                        onClick={() => {
                                          setQuery(aiErrorAnalysis.corrected_query)
                                          toast.success('Corrected query applied to editor!')
                                        }}
                                        className='bg-purple-600 hover:bg-purple-500 text-white text-xs h-7 px-2.5 gap-1 shadow-sm'
                                      >
                                        Apply Fix to Editor
                                      </Button>
                                    </div>
                                    <pre className='bg-slate-950 p-3 rounded-md font-mono text-xs text-green-400 overflow-x-auto border border-purple-900/40 whitespace-pre-wrap break-all'>
                                      {aiErrorAnalysis.corrected_query}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className='space-y-3'>
                                <div>
                                  <div className='font-semibold text-purple-300 text-xs uppercase tracking-wider mb-1 flex items-center gap-1.5'>
                                    <Sparkles className='h-3 w-3 text-purple-400' />
                                    <span>Probable Cause & Reason</span>
                                  </div>
                                  <div className='bg-purple-950/50 p-3 rounded-md border border-purple-800/40 leading-relaxed text-xs sm:text-sm text-purple-100'>
                                    <TypewriterText
                                      key={aiErrorAnalysis.cause}
                                      text={aiErrorAnalysis.cause}
                                      speed={12}
                                      onComplete={handleCauseComplete}
                                    />
                                  </div>
                                </div>

                                {aiErrorAnalysis.corrected_query && causeFinished && (
                                  <div className='pt-1 animate-in fade-in slide-in-from-top-1 duration-300'>
                                    <div className='flex items-center justify-between mb-1.5'>
                                      <span className='font-semibold text-purple-300 text-xs uppercase tracking-wider flex items-center gap-1.5'>
                                        <Sparkles className='h-3 w-3 text-green-400' />
                                        <span>Suggested Correct Query</span>
                                      </span>
                                      <Button
                                        size='sm'
                                        onClick={() => {
                                          setQuery(aiErrorAnalysis.corrected_query)
                                          toast.success('Corrected query applied to editor!')
                                        }}
                                        className='bg-purple-600 hover:bg-purple-500 text-white text-xs h-7 px-2.5 gap-1 shadow-sm'
                                      >
                                        Apply Fix to Editor
                                      </Button>
                                    </div>
                                    <TypewriterText
                                      key={aiErrorAnalysis.corrected_query}
                                      text={aiErrorAnalysis.corrected_query}
                                      speed={15}
                                      isCode={true}
                                      onComplete={() => setAnalysisTypingDone(true)}
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className='text-red-400 text-xs'>
                            {aiErrorAnalysis.error || 'Failed to analyze error.'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {queryResult.success && queryResult.rows && (
                  <div className='mt-2 text-xs sm:text-sm opacity-70'>
                    {queryResult.rows.length} row(s) returned
                  </div>
                )}
              </Card>
            )}
          </div>

          {/* Query History & Saved Queries Sidebar */}
          <div className='lg:col-span-4 min-w-0'>
            <Card
              className={`border transition-colors duration-300 h-fit lg:sticky lg:top-20 min-w-0 ${activeThemeObj.styles?.cardBg || 'bg-white border-slate-200'}`}
              data-testid='history-panel'
            >
              <div className={`p-3 sm:p-4 border-b flex items-center justify-between gap-2 ${activeThemeObj.styles?.border || 'border-slate-200'}`}>
                <div className={`flex items-center gap-1 p-1 rounded-lg text-xs font-semibold ${activeThemeObj.isDark ? 'bg-slate-800/80 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                  <button
                    onClick={() => setSidebarTab('history')}
                    data-testid='history-tab'
                    className={`px-2.5 py-1 rounded-md transition-all flex items-center gap-1.5 ${
                      sidebarTab === 'history'
                        ? activeThemeObj.isDark ? 'bg-slate-950 text-blue-400 shadow-sm font-bold' : 'bg-white text-blue-600 shadow-sm font-bold'
                        : 'hover:opacity-100 opacity-70'
                    }`}
                  >
                    <History className='h-3.5 w-3.5' />
                    <span>History</span>
                    {history.length > 0 && (
                      <span className={`px-1.5 py-0.2 text-[10px] rounded-full ${activeThemeObj.isDark ? 'bg-slate-800 text-slate-300' : 'bg-slate-200 text-slate-700'}`}>
                        {history.length}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setSidebarTab('saved')}
                    data-testid='saved-queries-tab'
                    className={`px-2.5 py-1 rounded-md transition-all flex items-center gap-1.5 ${
                      sidebarTab === 'saved'
                        ? activeThemeObj.isDark ? 'bg-slate-950 text-blue-400 shadow-sm font-bold' : 'bg-white text-blue-600 shadow-sm font-bold'
                        : 'hover:opacity-100 opacity-70'
                    }`}
                  >
                    <Bookmark className='h-3.5 w-3.5' />
                    <span>Saved Queries</span>
                    {savedQueries.length > 0 && (
                      <span className='bg-blue-500/20 text-blue-400 px-1.5 py-0.2 text-[10px] rounded-full font-bold'>
                        {savedQueries.length}
                      </span>
                    )}
                  </button>
                </div>
                {sidebarTab === 'history' && (
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={() => setShowClearHistoryDialog(true)}
                    data-testid='clear-history-button'
                    className='h-8 w-8 p-0 opacity-70 hover:opacity-100 hover:text-red-500'
                    title='Clear History'
                  >
                    <Trash2 className='h-4 w-4' />
                  </Button>
                )}
              </div>

              <ScrollArea className='h-[300px] sm:h-[450px] lg:h-[calc(100vh-220px)] max-h-[600px]'>
                <div className='p-3 sm:p-4 space-y-2'>
                  {sidebarTab === 'history' ? (
                    history.length === 0 ? (
                      <p className='text-xs sm:text-sm opacity-60 text-center py-8'>
                        No query history yet
                      </p>
                    ) : (
                      history.map(item => (
                        <div
                          key={item.id}
                          className={`group relative rounded-lg border transition-all p-2.5 sm:p-3 ${
                            activeThemeObj.isDark
                              ? 'border-slate-800/80 bg-slate-950/40 hover:border-blue-500/50 hover:bg-slate-800/40'
                              : 'border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/50'
                          }`}
                          data-testid={`history-item-${item.id}`}
                        >
                          <div className='flex items-start justify-between gap-2 mb-1.5'>
                            <button
                              onClick={() => loadQueryFromHistory(item.query)}
                              className='flex items-center gap-1.5 min-w-0 text-left flex-1'
                            >
                              {item.success ? (
                                <CheckCircle className='h-3.5 w-3.5 text-green-500 flex-shrink-0' />
                              ) : (
                                <XCircle className='h-3.5 w-3.5 text-red-500 flex-shrink-0' />
                              )}
                              <span className='text-xs opacity-70 flex items-center gap-1 truncate'>
                                <Clock className='h-3 w-3 flex-shrink-0' />
                                {new Date(item.executed_at).toLocaleTimeString()}
                              </span>
                            </button>
                            <div className='flex items-center gap-1 flex-shrink-0'>
                              {item.execution_time_ms !== null && (
                                <span className='text-xs opacity-60'>
                                  {item.execution_time_ms === 0
                                    ? '0ms'
                                    : `${item.execution_time_ms.toFixed(0)}ms`}
                                </span>
                              )}
                              <Button
                                variant='ghost'
                                size='sm'
                                onClick={(e) => {
                                  e.stopPropagation()
                                  openSaveDialog(item.query)
                                }}
                                title='Save Query with Label'
                                data-testid={`save-history-item-${item.id}`}
                                className='h-6 w-6 p-0 opacity-60 hover:opacity-100 hover:text-blue-500 hover:bg-blue-500/10 rounded-md'
                              >
                                <Bookmark className='h-3.5 w-3.5' />
                              </Button>
                            </div>
                          </div>
                          <code
                            onClick={() => loadQueryFromHistory(item.query)}
                            className={`text-xs line-clamp-2 block break-all font-mono cursor-pointer ${
                              activeThemeObj.isDark ? 'text-slate-200' : 'text-slate-700'
                            }`}
                          >
                            {item.query}
                          </code>
                          {item.error_message && (
                            <p className='text-xs text-red-500 mt-1 line-clamp-1 break-all'>
                              {item.error_message}
                            </p>
                          )}
                        </div>
                      ))
                    )
                  ) : (
                    savedQueries.length === 0 ? (
                      <p className='text-xs sm:text-sm opacity-60 text-center py-8'>
                        No saved queries yet. Click the bookmark icon <Bookmark className='inline h-3.5 w-3.5 text-blue-500 ml-0.5' /> on any history item to save it!
                      </p>
                    ) : (
                      savedQueries.map(item => (
                        <div
                          key={item.id}
                          data-testid={`saved-query-item-${item.id}`}
                          className={`rounded-lg border transition-all p-2.5 sm:p-3 space-y-1.5 ${
                            activeThemeObj.isDark
                              ? 'border-blue-900/40 bg-slate-950/60 hover:border-blue-700/60 hover:shadow-md'
                              : 'border-blue-100 bg-white hover:border-blue-300 hover:shadow-sm'
                          }`}
                        >
                          <div className='flex items-center justify-between gap-2'>
                            <div className='flex items-center gap-1.5 min-w-0'>
                              <Tag className='h-3.5 w-3.5 text-blue-500 flex-shrink-0' />
                              <span className='text-xs sm:text-sm font-semibold truncate' title={item.label}>
                                {item.label}
                              </span>
                            </div>
                            <div className='flex items-center gap-1 flex-shrink-0'>
                              <Button
                                variant='ghost'
                                size='sm'
                                onClick={() => openEditLabelDialog(item)}
                                title='Edit Label'
                                data-testid={`edit-saved-label-${item.id}`}
                                className='h-6 w-6 p-0 opacity-60 hover:opacity-100 hover:text-blue-500 rounded-md'
                              >
                                <Pencil className='h-3 w-3' />
                              </Button>
                              <Button
                                variant='ghost'
                                size='sm'
                                onClick={() => confirmDeleteSavedQuery(item.id)}
                                title='Delete Saved Query'
                                data-testid={`delete-saved-query-${item.id}`}
                                className='h-6 w-6 p-0 opacity-60 hover:opacity-100 hover:text-red-500 rounded-md'
                              >
                                <Trash2 className='h-3 w-3' />
                              </Button>
                            </div>
                          </div>
                          <code
                            onClick={() => {
                              setQuery(item.query)
                              toast.info('Saved query loaded into editor')
                            }}
                            className={`text-xs line-clamp-2 block break-all font-mono p-2 rounded border cursor-pointer ${
                              activeThemeObj.isDark
                                ? 'bg-slate-900/80 text-slate-200 border-slate-800 hover:bg-slate-800/80'
                                : 'bg-slate-50 text-slate-700 border-slate-100 hover:bg-blue-50/50'
                            }`}
                            title='Click to load query into editor'
                          >
                            {item.query}
                          </code>
                          <div className='flex items-center justify-between pt-0.5 text-[11px] opacity-60'>
                            <span className='flex items-center gap-1'>
                              <Clock className='h-2.5 w-2.5' />
                              {new Date(item.saved_at).toLocaleDateString()} {new Date(item.saved_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <button
                              onClick={() => {
                                setQuery(item.query)
                                toast.info('Saved query loaded into editor')
                              }}
                              className='text-blue-500 font-medium hover:underline text-[11px]'
                            >
                              Load Query
                            </button>
                          </div>
                        </div>
                      ))
                    )
                  )}
                </div>
              </ScrollArea>
            </Card>
          </div>
        </div>
      </div>

      {/* Save Query Dialog */}
      <AlertDialog open={showSaveQueryDialog} onOpenChange={setShowSaveQueryDialog}>
        <AlertDialogContent data-testid='save-query-dialog' className='max-w-[95vw] sm:max-w-lg min-w-0 overflow-hidden'>
          <AlertDialogHeader className='min-w-0'>
            <AlertDialogTitle className='flex items-center gap-2 truncate'>
              <Bookmark className='h-5 w-5 text-blue-600 flex-shrink-0' />
              Save Query with Label
            </AlertDialogTitle>
            <AlertDialogDescription>
              Give this query a custom label so you can quickly find and reuse it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className='space-y-3 py-2 min-w-0 w-full'>
            <div className='min-w-0 w-full'>
              <label className={`block text-xs font-semibold mb-1 ${activeThemeObj.isDark ? 'text-slate-300' : 'text-slate-700'}`}>Query Label</label>
              <Input
                id='save-label-input'
                placeholder='e.g., Get active students list'
                value={saveLabelInput}
                onChange={e => setSaveLabelInput(e.target.value)}
                data-testid='save-query-label-input'
                onKeyPress={e => {
                  if (e.key === 'Enter' && !isSavingQuery) {
                    handleSaveQuerySubmit();
                  }
                }}
              />
            </div>
            <div className='min-w-0 w-full'>
              <label className={`block text-xs font-semibold mb-1 ${activeThemeObj.isDark ? 'text-slate-300' : 'text-slate-700'}`}>SQL Code</label>
              <pre className='text-xs font-mono bg-slate-900 text-slate-100 p-2.5 rounded-md overflow-x-auto max-h-40 whitespace-pre-wrap break-all w-full max-w-full'>
                {selectedHistoryQuery}
              </pre>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setShowSaveQueryDialog(false);
                setSaveLabelInput('');
              }}
              data-testid='save-query-cancel-button'
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSaveQuerySubmit}
              disabled={isSavingQuery}
              data-testid='save-query-confirm-button'
            >
              Save Query
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Saved Query Label Dialog */}
      <AlertDialog open={showEditLabelDialog} onOpenChange={setShowEditLabelDialog}>
        <AlertDialogContent data-testid='edit-label-dialog' className='max-w-[95vw] sm:max-w-lg'>
          <AlertDialogHeader>
            <AlertDialogTitle className='flex items-center gap-2'>
              <Pencil className='h-5 w-5 text-blue-600' />
              Edit Query Label
            </AlertDialogTitle>
            <AlertDialogDescription>
              Update the label for this saved query.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className='py-2 space-y-2'>
            <Input
              id='edit-label-input'
              placeholder='e.g., Updated query title'
              value={editLabelInput}
              onChange={e => setEditLabelInput(e.target.value)}
              data-testid='edit-query-label-input'
              onKeyPress={e => {
                if (e.key === 'Enter') {
                  handleUpdateLabelSubmit();
                }
              }}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setShowEditLabelDialog(false)}
              data-testid='edit-label-cancel-button'
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUpdateLabelSubmit}
              data-testid='edit-label-confirm-button'
            >
              Update Label
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Saved Query Dialog */}
      <AlertDialog open={showDeleteSavedDialog} onOpenChange={setShowDeleteSavedDialog}>
        <AlertDialogContent data-testid='delete-saved-dialog' className='max-w-[95vw] sm:max-w-lg'>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Saved Query?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this saved query? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid='delete-saved-cancel-button'>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSavedQuery}
              data-testid='delete-saved-confirm-button'
              className='bg-red-600 hover:bg-red-700 text-white'
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Database Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent data-testid='reset-dialog' className='max-w-[95vw] sm:max-w-lg'>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Database?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete all your current data and restore the database to
              its initial state with sample data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid='reset-cancel-button'>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={resetDatabase}
              data-testid='reset-confirm-button'
            >
              Reset Database
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clear History Dialog */}
      <AlertDialog
        open={showClearHistoryDialog}
        onOpenChange={setShowClearHistoryDialog}
      >
        <AlertDialogContent data-testid='clear-history-dialog' className='max-w-[95vw] sm:max-w-lg'>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear Query History?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all your query history. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid='clear-history-cancel-button'>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={clearHistory}
              data-testid='clear-history-confirm-button'
            >
              Clear History
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AI Prompt Dialog */}
      <AlertDialog open={showAiPromptDialog} onOpenChange={setShowAiPromptDialog}>
        <AlertDialogContent data-testid='ai-prompt-dialog' className='max-w-[95vw] sm:max-w-lg'>
          <AlertDialogHeader>
            <AlertDialogTitle>AI SQL Assistant</AlertDialogTitle>
            <AlertDialogDescription>
              Enter a natural language prompt to generate an SQL query.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className='grid gap-4 py-4'>
            <Input
              id='ai-prompt'
              placeholder='e.g., "Show all records in the students table"'
              className='col-span-3'
              value={aiPrompt}
              onChange={e => setAiPrompt(e.target.value)}
              onKeyPress={e => {
                if (e.key === 'Enter' && !generatingSql) {
                  handleGenerateSql();
                }
              }}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setAiPrompt('');
                setShowAiPromptDialog(false);
              }}
              data-testid='ai-prompt-cancel-button'
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleGenerateSql}
              disabled={generatingSql}
              data-testid='ai-prompt-generate-button'
            >
              {generatingSql ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Generating...
                </>
              ) : (
                'Generate SQL'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Settings & Theme Selection Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent data-testid='settings-dialog' className='max-w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto p-4 sm:p-6'>
          <DialogHeader className='pb-2 border-b border-border/40'>
            <DialogTitle className='flex items-center gap-2 text-xl font-bold'>
              <Settings className='h-5 w-5 text-purple-600 dark:text-purple-400' />
              Workspace Settings
            </DialogTitle>
            <DialogDescription>
              Manage AI assistant behavior and customize workspace appearance themes.
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue='ai' className='w-full pt-2'>
            <TabsList className='grid w-full grid-cols-2 mb-4 bg-muted/60 p-1 rounded-xl'>
              <TabsTrigger value='ai' className='gap-2 text-xs sm:text-sm py-2 rounded-lg data-[state=active]:bg-background shadow-xs'>
                <Sparkles className='h-4 w-4 text-purple-600 dark:text-purple-400' />
                <span>AI Preferences</span>
              </TabsTrigger>
              <TabsTrigger value='appearance' className='gap-2 text-xs sm:text-sm py-2 rounded-lg data-[state=active]:bg-background shadow-xs'>
                <Palette className='h-4 w-4 text-blue-600 dark:text-blue-400' />
                <span>Appearance & Themes</span>
              </TabsTrigger>
            </TabsList>

            {/* AI Settings Tab */}
            <TabsContent value='ai' className='space-y-4 outline-none'>
              <div className='p-4 rounded-xl border border-purple-200 dark:border-purple-800/40 bg-purple-50/70 dark:bg-purple-950/20 space-y-4 shadow-xs'>
                <div className='flex items-start justify-between gap-4'>
                  <div className='space-y-1.5'>
                    <div className='flex items-center gap-2 font-semibold text-sm text-purple-950 dark:text-purple-300'>
                      <Sparkles className='h-4 w-4 text-purple-600 dark:text-purple-400' />
                      <span>Auto-Analyze Errors with AI</span>
                      {autoExplainError ? (
                        <Badge className='bg-purple-100 text-purple-800 border-purple-300/80 dark:bg-purple-500/20 dark:text-purple-300 dark:border-purple-500/40 text-[10px] px-2 py-0.5 font-medium'>
                          Automatic
                        </Badge>
                      ) : (
                        <Badge variant='outline' className='bg-white/80 text-slate-700 border-slate-300 dark:bg-slate-800/60 dark:text-slate-300 dark:border-slate-700 text-[10px] px-2 py-0.5 font-medium'>
                          On Demand
                        </Badge>
                      )}
                    </div>
                    <p className='text-xs leading-relaxed text-slate-600 dark:text-slate-400'>
                      When enabled, DataPilot will automatically run AI diagnostics as soon as a query error occurs. When disabled, you can manually click the "Explain & Fix Error with AI" button whenever needed.
                    </p>
                  </div>
                  <Switch
                    checked={autoExplainError}
                    onCheckedChange={handleToggleAutoExplainError}
                    className='mt-1'
                  />
                </div>

                <div className='pt-2 border-t border-purple-200 dark:border-purple-800/30 flex items-center justify-between text-xs text-purple-900/80 dark:text-purple-300/80'>
                  <span className='font-medium'>Current Behavior:</span>
                  <span className='font-mono text-[11px] bg-purple-100/90 text-purple-900 border-purple-300/70 dark:bg-purple-900/40 dark:text-purple-200 dark:border-purple-800/40 px-2.5 py-1 rounded-md border font-semibold'>
                    {autoExplainError ? '⚡ Instant Auto-Diagnosis' : '👆 Click Button On Demand'}
                  </span>
                </div>
              </div>

              <div className='p-4 rounded-xl border border-border/60 bg-muted/30 dark:bg-muted/20 space-y-2 text-xs text-slate-600 dark:text-slate-400'>
                <div className='font-semibold text-foreground flex items-center gap-1.5 text-xs'>
                  <Sparkles className='h-3.5 w-3.5 text-purple-600 dark:text-purple-400' />
                  <span>AI Diagnostics Features</span>
                </div>
                <ul className='list-disc list-inside space-y-1 pl-1 text-[11px] leading-relaxed'>
                  <li>Explains the probable cause of SQL errors in clear language.</li>
                  <li>Addresses you directly ("You intended to sort...").</li>
                  <li>Provides a suggested corrected query with a one-click apply fix button.</li>
                </ul>
              </div>
            </TabsContent>

            {/* Appearance Tab */}
            <TabsContent value='appearance' className='space-y-4 outline-none'>
              <div className='flex items-center justify-between'>
                <h3 className='text-xs font-semibold tracking-wider uppercase opacity-75'>
                  Choose Theme ({themes.length})
                </h3>
              </div>

              <div className='grid grid-cols-1 sm:grid-cols-2 gap-3.5'>
                {themes.map((t) => {
                  const isSelected = theme === t.id;
                  return (
                    <div
                      key={t.id}
                      onClick={() => setTheme(t.id)}
                      data-testid={`theme-option-${t.id}`}
                      className={`group relative border-2 rounded-xl p-3.5 cursor-pointer transition-all duration-200 hover:scale-[1.02] flex flex-col justify-between ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50/20 shadow-md ring-2 ring-blue-500/20'
                          : 'border-border/60 hover:border-border hover:shadow-sm'
                      }`}
                    >
                      {/* Header: Name & Check */}
                      <div>
                        <div className='flex items-center justify-between mb-1.5'>
                          <div className='flex items-center gap-2 font-semibold text-sm'>
                            <span>{t.name}</span>
                            {t.isDark ? (
                              <span className='text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700 font-normal'>Dark</span>
                            ) : (
                              <span className='text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-200 font-normal'>Light</span>
                            )}
                          </div>
                          {isSelected && (
                            <div className='h-5 w-5 rounded-full bg-blue-500 text-white flex items-center justify-center shadow-xs'>
                              <Check className='h-3.5 w-3.5' />
                            </div>
                          )}
                        </div>
                        <p className='text-xs opacity-75 mb-3 line-clamp-2 leading-relaxed'>
                          {t.description}
                        </p>
                      </div>

                      {/* Preview Swatch */}
                      <div className={`h-12 w-full rounded-lg border ${t.preview.border} ${t.preview.bg} p-2 flex items-center justify-between overflow-hidden shadow-inner`}>
                        <div className={`h-full w-1/3 rounded ${t.preview.header} border border-slate-700/20 flex items-center px-1.5`}>
                          <div className='h-2 w-2 rounded-full bg-blue-400' />
                        </div>
                        <div className={`h-6 px-2.5 rounded text-[10px] font-medium flex items-center justify-center text-white ${t.preview.accent}`}>
                          Preview
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default WorkspacePage;
